import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatStepperModule, MatStepper } from '@angular/material/stepper';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { Country } from 'country-state-city';

interface CsvDialogData {
  entityType: 'Companies' | 'Individuals' | 'Vessels';
}

interface CsvRow {
  [key: string]: string;
}

interface FieldMapping {
  csvField: string;
  targetField: string;
  required: boolean;
}

interface CountryMappingResult {
  success: boolean;
  isoCode?: string;
  countryName?: string;
  originalValue: string;
}

@Component({
  selector: 'app-csv-upload-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatStepperModule,
    MatSnackBarModule,
    MatCardModule,
    MatDividerModule
  ],
  template: `
    <div class="csv-upload-dialog">
      <mat-dialog-content>
        <mat-horizontal-stepper #stepper>
          <!-- Step 1: File Upload -->
          <mat-step label="Upload CSV File">
            <div class="upload-section">
              <h3 class="text-lg font-semibold mb-4">Upload CSV File for {{ data.entityType }}</h3>
              
              <div class="mb-4 p-3 bg-gray-100 border border-primary/40 rounded-lg">
                <p class="text-sm text-primary flex items-center">
                  Need help with the format? Download our template below to see the expected structure.
                </p>
                <p class="text-xs text-primary">
                  Required fields for {{ data.entityType.toLowerCase() }}: 
                  <span *ngIf="data.entityType === 'Companies'">Company Name, Country, Address</span>
                  <span *ngIf="data.entityType === 'Individuals'">Full Name, Country</span>
                  <span *ngIf="data.entityType === 'Vessels'">Vessel Name, IMO Number</span>
                </p>
                <p class="text-xs text-orange-600 mt-2 flex items-center gap-1">
                  Maximum file size: 1000 rows
                </p>
              </div>
              
              <!-- Drag and Drop Area -->
              <div 
                class="drop-zone"
                [class.dragover]="isDragOver"
                (dragover)="onDragOver($event)"
                (dragleave)="onDragLeave($event)"
                (drop)="onFileDropped($event)"
                (click)="fileInput.click()">
                
                <div class="drop-zone-content">
                  <mat-icon class="text-6xl text-gray-400 mb-4">cloud_upload</mat-icon>
                  <p class="text-lg mb-2">Drag and drop your CSV file here</p>
                  <p class="text-sm text-gray-500 mb-4">or click to browse</p>
                  <button mat-raised-button class="text-white" color="primary">
                    <mat-icon>upload_file</mat-icon>
                    Choose File
                  </button>
                </div>
                
                <input #fileInput type="file" accept=".csv" (change)="onFileSelected($event)" style="display: none;">
              </div>
              
              <div *ngIf="selectedFile" class="file-info mt-4">
                <mat-card>
                  <mat-card-content>
                    <div class="flex items-center gap-3">
                      <mat-icon color="primary">description</mat-icon>
                      <div>
                        <p class="font-medium">{{ selectedFile.name }}</p>
                        <p class="text-sm text-gray-500">{{ formatFileSize(selectedFile.size) }}</p>
                      </div>
                      <button mat-icon-button color="warn" (click)="removeFile()">
                        <mat-icon>close</mat-icon>
                      </button>
                    </div>
                  </mat-card-content>
                </mat-card>
              </div>
              
              <div class="mt-6">
                <button mat-stroked-button color="accent" (click)="downloadTemplate()">
                  <mat-icon>download</mat-icon>
                  Download Template
                </button>
              </div>
            </div>
          </mat-step>

          <!-- Step 2: Preview and Field Mapping -->
          <mat-step label="Map Fields" [completed]="mappingComplete">
            <div class="mapping-section" *ngIf="csvData.length > 0">
              <h3 class="text-lg font-semibold mb-4">Map CSV Fields to Required Fields</h3>
              <p class="text-sm text-gray-600 mb-6">Select the CSV column that matches each required field. The data preview below will update as you make your selections.</p>
              
              <!-- Preview Table with Inline Field Mapping -->
              <div class="preview-section">
                <div class="overflow-x-auto">
                  <table mat-table [dataSource]="previewData" class="w-full mapping-table">
                    <ng-container *ngFor="let field of targetFields" [matColumnDef]="field">
                      <th mat-header-cell *matHeaderCellDef class="mapping-header">
                        <!-- Field Mapping Dropdown -->
                        <div class="field-mapping-container">
                          <div class="field-label">
                            {{ getFieldLabel(field) }}
                            <span *ngIf="isFieldRequired(field)" class="text-red-500 ml-1">*</span>
                          </div>
                          <mat-form-field appearance="outline" class="mapping-select">
                            <mat-select [(value)]="fieldMappings[field]" 
                                        (selectionChange)="onFieldMappingChange(field, $event.value)"
                                        placeholder="Select CSV column">
                              <mat-option value="">-- Skip this field --</mat-option>
                              <mat-option *ngFor="let csvField of csvHeaders" [value]="csvField">
                                {{ csvField }}
                              </mat-option>
                            </mat-select>
                          </mat-form-field>
                          <div class="mapping-status" *ngIf="!fieldMappings[field] && isFieldRequired(field)">
                            <span class="text-xs text-red-600">
                              <mat-icon class="text-xs">error</mat-icon>
                              Required field - please select a column
                            </span>
                          </div>
                        </div>
                      </th>
                      <td mat-cell *matCellDef="let row" class="preview-cell">
                        <span class="cell-content" [class.empty-value]="!getMappedValue(row, field)">
                          {{ getMappedValue(row, field) || '(no data)' }}
                        </span>
                      </td>
                    </ng-container>
                    
                    <tr mat-header-row *matHeaderRowDef="targetFields"></tr>
                    <tr mat-row *matRowDef="let row; columns: targetFields;" class="preview-row"></tr>
                  </table>
                </div>
                
                <div class="preview-info mt-4 p-3 bg-gray-50 rounded-lg">
                  <p class="text-sm text-gray-600 flex items-center gap-1">
                    <mat-icon class="text-sm mr-1 flex items-center justify-center">info</mat-icon>
                    Showing first 5 rows of {{ csvData.length }} total records. 
                    <span class="font-medium">{{ validRowCount }} valid rows</span> will be imported based on current mapping.
                  </p>
                </div>
              </div>
              
              <div class="mt-6 flex justify-end">
                <button mat-raised-button class="text-white cursor-pointer" color="primary" 
                        [disabled]="!isValidMapping()" 
                        (click)="confirmImport()">
                  Import {{ validRowCount }} {{ data.entityType }}
                </button>
              </div>
            </div>
          </mat-step>
        </mat-horizontal-stepper>
      </mat-dialog-content>
      
      <mat-dialog-actions align="end">
        <button mat-button (click)="closeDialog()">Cancel</button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .csv-upload-dialog {
      width: 800px;
      max-width: 90vw;
      max-height: 90vh;
    }
    
    .drop-zone {
      border: 2px dashed #ccc;
      border-radius: 8px;
      padding: 40px;
      text-align: center;
      cursor: pointer;
      transition: all 0.3s ease;
    }
    
    .drop-zone:hover,
    .drop-zone.dragover {
      border-color: #2196F3;
      background-color: #f5f5f5;
    }
    
    .drop-zone-content {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    
    .mapping-row {
      margin-bottom: 16px;
    }
    
    .preview-section {
      max-height: 600px;
      overflow-y: auto;
    }
    
    .file-info {
      max-width: 300px;
    }

    /* Mapping table styles */
    .mapping-table {
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      overflow: hidden;
    }

    .mapping-header {
      background-color: #f8f9fa !important;
      border-bottom: 2px solid #e0e0e0 !important;
      padding: 16px 12px !important;
      vertical-align: top !important;
      min-width: 200px;
    }

    .field-mapping-container {
      display: flex;
      flex-direction: column;
      gap: 8px;
      min-height: 120px;
    }

    .field-label {
      font-weight: 600;
      font-size: 14px;
      color: #333;
      margin-bottom: 4px;
    }

    .mapping-select {
      width: 100%;
      min-width: 180px;
    }

    .mapping-select ::ng-deep .mat-form-field-wrapper {
      padding-bottom: 0;
    }

    .mapping-select ::ng-deep .mat-form-field-infix {
      padding: 8px 0;
      border-top: none;
    }

    .mapping-status {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-top: 4px;
    }

    .mapping-status mat-icon {
      font-size: 14px;
      width: 14px;
      height: 14px;
    }

    .preview-cell {
      padding: 12px !important;
      border-bottom: 1px solid #f0f0f0 !important;
      vertical-align: top !important;
      max-width: 200px;
      word-wrap: break-word;
    }

    .cell-content {
      display: block;
      font-size: 13px;
      line-height: 1.4;
    }

    .empty-value {
      color: #999;
      font-style: italic;
    }

    .preview-row:hover {
      background-color: #f9f9f9;
    }

    .preview-info {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    /* Fix stepper styling for better readability */
    ::ng-deep .mat-step-header .mat-step-icon {
      color: white !important;
    }

    ::ng-deep .mat-step-header .mat-step-icon .mat-icon {
      color: white !important;
    }

    ::ng-deep .mat-step-header .mat-step-icon-selected {
      color: white !important;
    }

    ::ng-deep .mat-step-header .mat-step-icon-state-done {
      color: white !important;
    }

    ::ng-deep .mat-step-header .mat-step-icon-state-edit {
      color: white !important;
    }
  `]
})
export class CsvUploadDialogComponent implements OnInit {
  @ViewChild('stepper') stepper!: MatStepper;
  
  selectedFile: File | null = null;
  isDragOver = false;
  csvData: CsvRow[] = [];
  csvHeaders: string[] = [];
  previewData: CsvRow[] = [];
  fieldMappings: { [key: string]: string } = {};
  processedData: any[] = [];
  mappingComplete = false;
  validRowCount = 0;
  invalidRowCount = 0;

  // Field definitions based on entity type
  companyFields = ['companyName', 'country', 'address', 'companySize', 'market'];
  individualFields = ['fullName', 'country', 'address'];
  vesselFields = ['name', 'imoNumber'];

  // Country mapping
  private countries: any[] = [];

  constructor(
    private dialogRef: MatDialogRef<CsvUploadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CsvDialogData,
    private fb: FormBuilder,
    private snackBar: MatSnackBar
  ) {
    this.countries = Country.getAllCountries();
  }

  ngOnInit(): void {
    this.initializeFieldMappings();
  }

  get targetFields(): string[] {
    if (this.data.entityType === 'Companies') {
      return this.companyFields;
    } else if (this.data.entityType === 'Individuals') {
      return this.individualFields;
    } else {
      return this.vesselFields;
    }
  }

  private initializeFieldMappings(): void {
    this.targetFields.forEach(field => {
      this.fieldMappings[field] = '';
    });
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  onFileDropped(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.handleFile(file);
    }
  }

  private handleFile(file: File): void {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.snackBar.open('Please select a CSV file', 'Close', { duration: 3000 });
      return;
    }
    
    this.selectedFile = file;
    // Auto-parse CSV and move to next step
    this.parseCSV();
  }

  removeFile(): void {
    this.selectedFile = null;
    this.csvData = [];
    this.csvHeaders = [];
    this.previewData = [];
  }

  parseCSV(): void {
    if (!this.selectedFile) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const csv = e.target?.result as string;
      this.processCsvText(csv);
      // Auto-advance to next step after parsing
      if (this.csvData.length > 0) {
        this.stepper.next();
      }
    };
    reader.readAsText(this.selectedFile);
  }

  private processCsvText(csv: string): void {
    const lines = csv.split('\n').map(line => line.trim()).filter(line => line);
    
    if (lines.length === 0) {
      this.snackBar.open('CSV file appears to be empty', 'Close', { duration: 3000 });
      return;
    }

    // Check row limit (subtract 1 for header row)
    const dataRowCount = lines.length - 1;
    if (dataRowCount > 1000) {
      this.snackBar.open(
        `CSV file contains ${dataRowCount} rows. Maximum allowed is 1000 rows. Please reduce the file size and try again.`, 
        'Close', 
        { duration: 5000 }
      );
      this.removeFile();
      return;
    }

    // Parse headers
    this.csvHeaders = this.parseCsvLine(lines[0]);
    
    // Parse data rows
    this.csvData = [];
    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCsvLine(lines[i]);
      if (values.length === this.csvHeaders.length) {
        const row: CsvRow = {};
        this.csvHeaders.forEach((header, index) => {
          row[header] = values[index];
        });
        this.csvData.push(row);
      }
    }

    // Apply fuzzy matching for initial suggestions
    this.applySuggestedMappings();
    
    // Create preview data (first 5 rows)
    this.previewData = this.csvData.slice(0, 5);
    
    // Update validation state after auto-mapping
    this.validateMapping();
    this.calculateValidRowCount();
    
    this.snackBar.open(`Parsed ${this.csvData.length} rows`, 'Close', { duration: 2000 });
  }

  private parseCsvLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current.trim());
    return result;
  }

  private applySuggestedMappings(): void {
    const fuzzyMatch = (target: string, options: string[]): string => {
      const targetLower = target.toLowerCase();
      
      // Exact match first
      for (const option of options) {
        if (option.toLowerCase() === targetLower) {
          return option;
        }
      }
      
      // Partial match
      for (const option of options) {
        if (option.toLowerCase().includes(targetLower) || targetLower.includes(option.toLowerCase())) {
          return option;
        }
      }
      
      // Fuzzy matching for common variations
      const variations: { [key: string]: string[] } = {
        companyName: ['company', 'name', 'business', 'organization', 'firm'],
        fullName: ['name', 'full name', 'person', 'individual'],
        name: ['vessel', 'vessel name', 'ship', 'ship name', 'name'],
        imoNumber: ['imo', 'imo number', 'imo no', 'imo#', 'imonumber'],
        country: ['country', 'nation', 'location'],
        address: ['address', 'location', 'street'],
        companySize: ['size', 'company size', 'business size'],
        market: ['market', 'industry', 'sector', 'business type']
      };
      
      if (variations[target]) {
        for (const variant of variations[target]) {
          for (const option of options) {
            if (option.toLowerCase().includes(variant) || variant.includes(option.toLowerCase())) {
              return option;
            }
          }
        }
      }
      
      return '';
    };

    this.targetFields.forEach(field => {
      this.fieldMappings[field] = fuzzyMatch(field, this.csvHeaders);
    });
  }

  onFieldMappingChange(targetField: string, csvField: string): void {
    this.fieldMappings[targetField] = csvField;
    this.updatePreview();
  }

  private updatePreview(): void {
    this.previewData = this.csvData.slice(0, 5);
    this.validateMapping();
    this.calculateValidRowCount();
  }

  private validateMapping(): boolean {
    const requiredFields = this.targetFields.filter(field => this.isFieldRequired(field));
    const mappedRequiredFields = requiredFields.filter(field => this.fieldMappings[field]);
    
    this.mappingComplete = mappedRequiredFields.length === requiredFields.length;
    return this.mappingComplete;
  }

  isValidMapping(): boolean {
    return this.validateMapping();
  }

  private calculateValidRowCount(): void {
    // Simple count - no validation, just count rows with required field mappings
    let validCount = 0;
    
    for (const row of this.csvData) {
      let hasRequiredFields = true;
      
      // Check if required fields have mappings (not validation of content)
      for (const targetField of this.targetFields) {
        if (this.isFieldRequired(targetField)) {
          const csvField = this.fieldMappings[targetField];
          if (!csvField) {
            hasRequiredFields = false;
            break;
          }
        }
      }
      
      if (hasRequiredFields) {
        validCount++;
      }
    }
    
    this.validRowCount = validCount;
  }

  getMappedValue(row: CsvRow, targetField: string): string {
    const csvField = this.fieldMappings[targetField];
    const value = csvField ? (row[csvField] || '') : '';
    
    // Special handling for country field to show mapping preview
    if (targetField === 'country' && value) {
      const mappingResult = this.mapCountryToIsoCode(value);
      if (mappingResult.success) {
        return `${value} → ${mappingResult.isoCode}`;
      } else {
        return `${value}`;
      }
    }
    
    return value;
  }

  getFieldLabel(field: string): string {
    const labels: { [key: string]: string } = {
      companyName: 'Company Name',
      fullName: 'Full Name',
      name: 'Vessel Name',
      imoNumber: 'IMO Number',
      country: 'Country',
      address: 'Address',
      companySize: 'Company Size',
      market: 'Market'
    };
    return labels[field] || field;
  }

  isFieldRequired(field: string): boolean {
    if (this.data.entityType === 'Companies') {
      return ['companyName', 'country', 'companySize', 'market'].includes(field);
    } else if (this.data.entityType === 'Individuals') {
      return ['fullName', 'country'].includes(field);
    } else {
      return ['name', 'imoNumber'].includes(field);
    }
  }

  /**
   * Maps a country name/code to ISO code using multiple matching strategies
   */
  private mapCountryToIsoCode(countryValue: string): CountryMappingResult {
    if (!countryValue || !countryValue.trim()) {
      return {
        success: false,
        originalValue: countryValue
      };
    }

    const searchValue = countryValue.trim();
    
    // Strategy 1: Check if it's already an ISO code
    const byIsoCode = this.countries.find(country => 
      country.isoCode.toLowerCase() === searchValue.toLowerCase()
    );
    if (byIsoCode) {
      return {
        success: true,
        isoCode: byIsoCode.isoCode,
        countryName: byIsoCode.name,
        originalValue: countryValue
      };
    }

    // Strategy 2: Exact name match
    const byExactName = this.countries.find(country => 
      country.name.toLowerCase() === searchValue.toLowerCase()
    );
    if (byExactName) {
      return {
        success: true,
        isoCode: byExactName.isoCode,
        countryName: byExactName.name,
        originalValue: countryValue
      };
    }

    // Strategy 3: Common country name variations
    const commonVariations: { [key: string]: string } = {
      // United States variations
      'usa': 'US',
      'united states': 'US',
      'united states of america': 'US',
      'america': 'US',
      'u.s.': 'US',
      'u.s.a.': 'US',
      
      // United Kingdom variations
      'uk': 'GB',
      'united kingdom': 'GB',
      'great britain': 'GB',
      'britain': 'GB',
      'england': 'GB',
      
      // Other common variations
      'russia': 'RU',
      'russian federation': 'RU',
      'south korea': 'KR',
      'korea south': 'KR',
      'republic of korea': 'KR',
      'north korea': 'KP',
      'korea north': 'KP',
      'china': 'CN',
      'peoples republic of china': 'CN',
      'taiwan': 'TW',
      'republic of china': 'TW',
      'iran': 'IR',
      'islamic republic of iran': 'IR',
      'syria': 'SY',
      'syrian arab republic': 'SY',
      'vietnam': 'VN',
      'viet nam': 'VN',
      'czech republic': 'CZ',
      'czechia': 'CZ',
      'macedonia': 'MK',
      'north macedonia': 'MK',
      'republic of north macedonia': 'MK',
      'myanmar': 'MM',
      'burma': 'MM',
      'congo': 'CG',
      'democratic republic of congo': 'CD',
      'congo drc': 'CD',
      'drc': 'CD'
    };

    const variationMatch = commonVariations[searchValue.toLowerCase()];
    if (variationMatch) {
      const country = this.countries.find(c => c.isoCode === variationMatch);
      if (country) {
        return {
          success: true,
          isoCode: country.isoCode,
          countryName: country.name,
          originalValue: countryValue
        };
      }
    }

    // Strategy 4: Partial name match (fuzzy matching)
    const byPartialName = this.countries.find(country => 
      country.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      searchValue.toLowerCase().includes(country.name.toLowerCase())
    );
    if (byPartialName) {
      return {
        success: true,
        isoCode: byPartialName.isoCode,
        countryName: byPartialName.name,
        originalValue: countryValue
      };
    }

    // No match found
    return {
      success: false,
      originalValue: countryValue
    };
  }

  processData(): void {
    this.processedData = [];
    this.invalidRowCount = 0;

    for (const row of this.csvData) {
      const processedRow: any = {
        id: this.generateId()
      };

      // Map all fields - no validation, just mapping
      for (const targetField of this.targetFields) {
        const csvField = this.fieldMappings[targetField];
        let value = csvField ? (row[csvField] || '') : '';

        // Special handling for specific fields
        if (targetField === 'country' && this.data.entityType !== 'Vessels') {
          const countryMappingResult = this.mapCountryToIsoCode(value);
          if (countryMappingResult.success) {
            processedRow.country = countryMappingResult.isoCode;
            processedRow.countryDisplay = countryMappingResult.countryName;
          } else {
            // Store original value - validation will happen in search form
            processedRow.country = value;
            processedRow.countryDisplay = value;
          }
        } else {
          processedRow[targetField] = value;
        }
      }

      // Always add the row - validation will happen in search form
      this.processedData.push(processedRow);
    }

    this.validRowCount = this.processedData.length;
    this.invalidRowCount = 0; // No validation here
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  confirmImport(): void {
    // Process the data before confirming import
    this.processData();
    
    this.dialogRef.close({
      entities: this.processedData,
      entityType: this.data.entityType
    });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  downloadTemplate(): void {
    const csvContent = this.generateTemplateCSV();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${this.data.entityType.toLowerCase()}_template.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    
    this.snackBar.open(`Downloaded ${this.data.entityType} template`, 'Close', { duration: 2000 });
  }

  private generateTemplateCSV(): string {
    let headers: string[];
    let sampleRows: string[][];

    if (this.data.entityType === 'Companies') {
      headers = ['Company Name', 'Country', 'Address', 'Company Size', 'Market'];
      sampleRows = [
        ['Acme Corporation', 'United States', '123 Business St, New York, NY 10001', 'Large', 'Technology'],
        ['Acme Corporation', 'US', '123 Business St, New York, NY 10001', 'Large', 'Technology'],
        ['Tech Solutions Ltd', 'United Kingdom', '456 London St, London SW1A 1AA', 'Medium', 'Technology'],
        ['Global Industries', 'Canada', '789 Toronto Ave, Toronto, ON M5V 3A8', 'Large', 'Manufacturing']
      ];
    } else if (this.data.entityType === 'Individuals') {
      headers = ['Full Name', 'Country', 'Address'];
      sampleRows = [
        ['John Smith', 'United States', '123 Main St, Chicago, IL 60601'],
        ['Sarah Johnson', 'Canada', '456 Maple Ave, Toronto, ON M4B 1B3'],
        ['Michael Brown', 'United Kingdom', '789 High St, London NW1 6XE']
      ];
    } else {
      headers = ['Vessel Name', 'IMO Number'];
      sampleRows = [
        ['ISLAND PERFORMER', '9682045'],
        ['OCEAN VOYAGER', '9123456'],
        ['SEA NAVIGATOR', '9234567']
      ];
    }

    // Convert to CSV format
    const csvRows = [headers.join(',')];
    
    sampleRows.forEach(row => {
      const escapedRow = row.map(field => {
        // Escape fields that contain commas, quotes, or newlines
        if (field.includes(',') || field.includes('"') || field.includes('\n')) {
          return `"${field.replace(/"/g, '""')}"`;
        }
        return field;
      });
      csvRows.push(escapedRow.join(','));
    });

    return csvRows.join('\n');
  }
} 